// Sitemap tab — a per-page table of every page/post on the connected site. The Run Crawl
// button + save icon live in App (so they reuse the standard toolbar); this component renders
// the table (and the empty / not-connected / crawling states).

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { EditorType } from "../lib/wpClient";

const POP_CANCEL_BTN: CSSProperties = { padding: "6px 12px", background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 0, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };
const POP_PRIMARY_BTN: CSSProperties = { padding: "6px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 0, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };

// Grow the edit textarea to fit its content so the full current value is visible without the
// user dragging the box. GROWS ONLY — never shrinks — so typing after a manual drag-resize keeps
// the size the user set (the CSS max-height + overflow cap the visible height). Runs as a ref
// callback (on mount) and on every change.
const autosizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  const current = el.offsetHeight;
  el.style.height = "auto";
  el.style.height = `${Math.max(current, el.scrollHeight)}px`;
};

export interface SitemapRow {
  id: number;
  title: string;
  url: string;
  type: "page" | "post";
  tags: string[];
  parent: number;   // parent page id (0 = top level); always 0 for posts
  slug: string;     // WP slug (last path segment)
  metaTitle: string;
  metaDescription: string;
  internalLinks: number;
  h1: number; h2: number; h3: number; h4: number; h5: number; h6: number;
  wordCount: number;
  statusCode: number | null;
  editorType?: EditorType; // how the page is built (LSG plugin v1.24.6+); undefined on older plugins
  hasSchema: boolean;   // rendered page emits any JSON-LD (from the crawl)
  schemaJson: string;   // the LSG _audit_schema blocks as editable JSON ("" if none)
  error?: string;
}

// The "Editor Type" column label + color. Undefined (older plugin / not yet crawled) → a muted dash.
export function editorTypeLabel(t?: EditorType): { label: string; color: string } {
  switch (t) {
    case "elementor":        return { label: "Elementor", color: "#c026d3" };
    case "other":            return { label: "Other", color: "#0d9488" };
    case "block":            return { label: "Block", color: "#2563eb" };
    case "classic-markdown": return { label: "Classic - Markdown", color: "#ea580c" };
    case "classic":          return { label: "Classic", color: "#475569" };
    default:                 return { label: "—", color: "#94a3b8" };
  }
}

// The Schema column's state, derived from the two stored fields above:
//   tool blocks present → LSG-added · else page emits ld+json → existing · else none.
export function schemaLabel(r: { hasSchema?: boolean; schemaJson?: string }): { label: string; color: string } {
  if ((r.schemaJson ?? "").trim()) return { label: "Yes - Added by LSG", color: "#16a34a" };
  if (r.hasSchema) return { label: "Yes - Existing", color: "#2563eb" };
  return { label: "No", color: "#94a3b8" };
}

interface Props {
  connected: boolean;
  rows: SitemapRow[];
  crawling: boolean;
  progress: { done: number; total: number } | null;
  crawledAt: string;
  selected: Set<string>;
  refreshing: Set<string>;
  onToggleSelect: (key: string) => void;
  onToggleAll: (keys: string[], checked: boolean) => void;
  onRefreshRow: (key: string) => void;
  onEditCell: (key: string, field: SitemapEditField, value: string) => void | Promise<void>;
  onEditParent: (key: string) => void; // hold the URL cell → open the Edit Page URL dialog
  onEditPage: (key: string) => void;   // "Edit Page" link → open the full Edit Page dialog
  redirectByPath?: Map<string, string>; // source path (normalized) → destination URL, for the Redirected column
}

const rowKey = (r: { type: string; id: number }) => `${r.type}-${r.id}`;

// Normalize a URL/path to a lowercase, trailing-slash-free path so it matches a stored redirect source.
const redirectKey = (u: string): string => {
  try { return (new URL(/^https?:\/\//i.test(u) ? u : `https://x${u.startsWith("/") ? "" : "/"}${u}`).pathname.replace(/\/+$/, "") || "/").toLowerCase(); }
  catch { return (u.replace(/[#?].*$/, "").replace(/\/+$/, "") || "/").toLowerCase(); }
};

const COLS = [
  "Page ID", "Page Title", "URL", "Redirected", "Page/Post Tags", "Meta Title", "Meta Description",
  "Editor Type", "Schema", "H1", "H2", "H3", "H4", "H5", "H6", "Word Count", "Status Code",
];

// Columns the user can sort: Page Title / URL alphabetically, Schema by its state.
const SORT_KEYS: Record<string, "title" | "url" | "schema" | "editor"> = { "Page Title": "title", "URL": "url", "Editor Type": "editor", "Schema": "schema" };

// Fields that can be edited inline (hold a cell ~0.2s → Edit?). Keyed by the cellsFor() data-column
// index: 1 Page Title, 4 Page/Post Tags, 5 Meta Title, 6 Meta Description, 8 Schema. (Index 3 is the
// read-only Redirected column; index 7 is the read-only Editor Type column.) Everything else (Page ID,
// URL, Redirected, Editor Type, H1–H6, Word Count, Status Code, and the action cells) is read-only.
export type SitemapEditField = "title" | "tags" | "metaTitle" | "metaDescription" | "schema";
const EDITABLE: Record<number, { field: SitemapEditField; label: string; multiline: boolean }> = {
  1: { field: "title", label: "Page Title", multiline: false },
  4: { field: "tags", label: "Page/Post Tags (comma-separated)", multiline: false },
  5: { field: "metaTitle", label: "Meta Title", multiline: true },
  6: { field: "metaDescription", label: "Meta Description", multiline: true },
  8: { field: "schema", label: "Schema — JSON-LD", multiline: true },
};
const HOLD_MS = 200;

function statusColor(code: number | null): string {
  if (code == null) return "#dc2626";
  if (code >= 200 && code < 300) return "#16a34a";
  if (code >= 300 && code < 400) return "#d97706";
  return "#dc2626";
}

// Copy text to the clipboard (async API with a hidden-textarea fallback for older/insecure ctx).
function fallbackCopy(text: string) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } catch { /* ignore */ }
  document.body.removeChild(ta);
}
function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

export const SitemapTab = memo(function SitemapTab({ connected, rows, crawling, progress, crawledAt, selected, refreshing, onToggleSelect, onToggleAll, onRefreshRow, onEditCell, onEditParent, onEditPage, redirectByPath }: Props) {
  // The table scrolls inside a height-capped box (scrollRef) so its own horizontal scrollbar
  // sits at the bottom of the visible area and stays put while you scroll the rows. A second
  // scrollbar above the table (topRef) mirrors it, so you can also scroll from the top.
  const topRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const activeScroller = useRef<HTMLElement | null>(null);
  const [tableWidth, setTableWidth] = useState(0);

  // ── Row virtualization (windowing) ────────────────────────────────────────────
  // A ~2800-row table is ~100k DOM nodes → the tab froze while the browser built it (opening the
  // tab) and tore it down (leaving it). Render only the rows in/near the viewport, with spacer rows
  // above/below to keep the scrollbar height + position correct. Scrolling, Search/Exclude, Bulk
  // Edit, selection and copy all still operate on the FULL list — only the rendered DOM is capped.
  const [rowHeight, setRowHeight] = useState(26);          // measured from a real row after first paint
  const [win, setWin] = useState({ start: 0, end: 80 });   // absolute row indices currently rendered
  const winRafRef = useRef<number | null>(null);

  // Sort by Page Title / URL (alphabetical) or Schema state (grouped) — click the header to toggle
  // asc → desc.
  const [sort, setSort] = useState<{ key: "title" | "url" | "schema" | "editor"; dir: "asc" | "desc" } | null>(null);
  const toggleSort = (key: "title" | "url" | "schema" | "editor") =>
    setSort(p => (p && p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  // Schema grouping order (asc): Added by LSG (0) → Existing (1) → No (2); desc reverses.
  const schemaRank = (r: SitemapRow) => ((r.schemaJson ?? "").trim() ? 0 : r.hasSchema ? 1 : 2);
  // Editor grouping order (asc): Markdown pages first (what the column is most useful for), then
  // Classic → Block → Elementor → Other, with unknown/"—" (older plugin) last.
  const editorRank = (r: SitemapRow) => {
    const order: Record<string, number> = { "classic-markdown": 0, classic: 1, block: 2, elementor: 3, other: 4 };
    return r.editorType && r.editorType in order ? order[r.editorType] : 5;
  };
  const displayRows = sort
    ? [...rows].sort((a, b) => {
        let c: number;
        if (sort.key === "schema") {
          c = schemaRank(a) - schemaRank(b);
          if (c === 0) c = a.url.localeCompare(b.url, undefined, { sensitivity: "base", numeric: true }); // stable within a group
        } else if (sort.key === "editor") {
          c = editorRank(a) - editorRank(b);
          if (c === 0) c = a.url.localeCompare(b.url, undefined, { sensitivity: "base", numeric: true }); // stable within a group
        } else {
          c = (sort.key === "title" ? a.title : a.url).localeCompare(
            sort.key === "title" ? b.title : b.url, undefined, { sensitivity: "base", numeric: true },
          );
        }
        return sort.dir === "asc" ? c : -c;
      })
    : rows;

  // Compute which absolute row indices to render from the scroll position + measured row height.
  const OVERSCAN = 12;
  const recomputeWindow = () => {
    const el = scrollRef.current;
    if (!el) return;
    const rh = rowHeight || 26;
    const total = displayRows.length;
    const start = Math.max(0, Math.floor(el.scrollTop / rh) - OVERSCAN);
    const count = Math.ceil(el.clientHeight / rh) + OVERSCAN * 2;
    const end = Math.min(total, start + count);
    setWin(prev => (prev.start === start && prev.end === end ? prev : { start, end }));
  };
  // rAF-throttled scroll → window update (cheap: only ~50 rows exist in the DOM).
  const onScrollVertical = () => {
    if (winRafRef.current != null) return;
    winRafRef.current = requestAnimationFrame(() => { winRafRef.current = null; recomputeWindow(); });
  };
  // Measure the real row height after paint, then fit the window to the viewport. Re-runs when the
  // row count or measured height changes, or when the table first appears (connected/crawling).
  useLayoutEffect(() => {
    const first = scrollRef.current?.querySelector<HTMLElement>("tbody tr[data-smrow]");
    if (first && first.offsetHeight > 0 && Math.abs(first.offsetHeight - rowHeight) > 1) setRowHeight(first.offsetHeight);
    recomputeWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows.length, rowHeight, connected, crawling]);
  // Recompute when the viewport is resized.
  useEffect(() => {
    const onResize = () => recomputeWindow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows.length, rowHeight]);

  // Keep the top scrollbar's spacer matched to the table's real width. Measured immediately,
  // again next frame, and again after a short delay (catches late layout when the tab is first
  // revealed or web fonts settle), plus on any size change — so the bar stays correct without
  // needing a tab-switch/remount to recover.
  useLayoutEffect(() => {
    const tableEl = tableRef.current;
    if (!tableEl) { setTableWidth(0); return; }
    const update = () => setTableWidth(tableEl.scrollWidth);
    update();
    const raf = requestAnimationFrame(update);
    const timer = setTimeout(update, 250);
    const ro = new ResizeObserver(update);
    ro.observe(tableEl);
    if (scrollRef.current) ro.observe(scrollRef.current);
    window.addEventListener("resize", update);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); ro.disconnect(); window.removeEventListener("resize", update); };
    // Re-measure when the table first appears too: on reconnect the table renders (connected
    // flips) while `rows` is unchanged, and after a crawl finishes — both need a fresh measure.
  }, [rows, connected, crawling]);

  // Mirror the scroll position between the top bar and the table box. Programmatic scrolls
  // fire async scroll events; the activeScroller guard (cleared next frame) makes those echoes
  // no-ops so the two bars don't fight each other.
  const syncFrom = (self: HTMLElement | null) => {
    if (!self) return;
    if (activeScroller.current && activeScroller.current !== self) return;
    activeScroller.current = self;
    const x = self.scrollLeft;
    for (const el of [topRef.current, scrollRef.current]) {
      if (el && el !== self && el.scrollLeft !== x) el.scrollLeft = x;
    }
    requestAnimationFrame(() => { activeScroller.current = null; });
  };

  // ── Spreadsheet-style cell selection ──────────────────────────────────────────
  // Click-drag a rectangle across ANY cells (any columns/rows), or click then shift-click to
  // extend across a scroll; Ctrl/Cmd+C copies the block as TSV. Native text selection is off
  // (CSS user-select:none) so the drag grabs whole cells, spreadsheet-style.
  const [sel, setSel] = useState<{ aR: number; aC: number; fR: number; fC: number } | null>(null);
  const dragging = useRef(false);
  const selBox = (s: { aR: number; aC: number; fR: number; fC: number }) => ({
    r0: Math.min(s.aR, s.fR), r1: Math.max(s.aR, s.fR),
    c0: Math.min(s.aC, s.fC), c1: Math.max(s.aC, s.fC),
  });
  const isSelected = (ri: number, ci: number) => {
    if (!sel) return false;
    const b = selBox(sel);
    return ri >= b.r0 && ri <= b.r1 && ci >= b.c0 && ci <= b.c1;
  };
  const startSel = (ri: number, ci: number, e: { shiftKey: boolean }) => {
    if (e.shiftKey && sel) setSel({ aR: sel.aR, aC: sel.aC, fR: ri, fC: ci });
    else setSel({ aR: ri, aC: ci, fR: ri, fC: ci });
    dragging.current = true;
    scrollRef.current?.focus();
  };
  const extendSel = (ri: number, ci: number) => {
    if (dragging.current) setSel(s => (s ? { ...s, fR: ri, fC: ci } : s));
    cancelHold(); // moving to another cell = a drag, not a hold
  };

  // ── Hold-to-edit ──────────────────────────────────────────────────────────────
  // Press and hold an editable cell for ~0.2s → an "Edit?" popup; Yes → an edit box above the
  // cell. Submit writes the change to WordPress (via onEditCell) and updates the table.
  type EditState = { key: string; field: SitemapEditField; kind: "field" | "parent"; label: string; multiline: boolean; value: string; rect: DOMRect };
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdFired = useRef(false); // set when a hold completes, so the ensuing click (e.g. a URL link) is swallowed
  const [editPrompt, setEditPrompt] = useState<EditState | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const cancelHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; } };
  const URL_COL = 2; // the URL column index in cellsFor() — held → Parent Page Edit
  const beginHold = (key: string, ci: number, text: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    cancelHold();
    if (ci === URL_COL) {
      // Holding the URL cell opens the Edit Page URL dialog (change parent / slug / add a redirect).
      holdTimer.current = setTimeout(() => {
        holdFired.current = true;
        setEditing(null);
        setEditPrompt({ key, field: "title", kind: "parent", label: "URL", multiline: false, value: text, rect });
      }, HOLD_MS);
      return;
    }
    const def = EDITABLE[ci];
    if (!def) return;
    holdTimer.current = setTimeout(() => {
      holdFired.current = true;
      setEditing(null);
      setEditPrompt({ key, field: def.field, kind: "field", label: def.label, multiline: def.multiline, value: text, rect });
    }, HOLD_MS);
  };

  // End the drag wherever the mouse is released; clear the selection when the rows change.
  useEffect(() => {
    const up = () => { dragging.current = false; cancelHold(); };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);
  useEffect(() => {
    setSel(null); setEditPrompt(null); setEditing(null);
    if (scrollRef.current) scrollRef.current.scrollTop = 0; // back to top on a new list
    setWin({ start: 0, end: 80 });
  }, [rows]);
  // Esc closes either popup.
  useEffect(() => {
    if (!editPrompt && !editing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setEditPrompt(null); setEditing(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editPrompt, editing]);

  // The data columns (in table order) — value for copy + node for display. Excludes the
  // checkbox/refresh action columns, so cell-selection indices line up with the data grid.
  const cellsFor = (r: SitemapRow): { text: string; node: ReactNode; cls: string; editValue?: string }[] => {
    const sc = schemaLabel(r);
    const redirectTo = redirectByPath?.get(redirectKey(r.url));
    return [
    { text: String(r.id), node: r.id, cls: "sm-num" },
    { text: r.title, node: r.title || "—", cls: "sm-title" },
    // URL — shown in red (sm-redir; theme-aware in app.css) when this URL 301-redirects elsewhere.
    { text: r.url, node: <a href={r.url} target="_blank" rel="noopener noreferrer" draggable={false} className={redirectTo ? "sm-redir" : undefined}>{r.url}</a>, cls: "sm-url" },
    // Redirected — the destination this URL 301-redirects to (from the Redirection Manager), else "—".
    { text: redirectTo ?? "", node: redirectTo ? <a href={redirectTo} target="_blank" rel="noopener noreferrer" draggable={false} className="sm-redir">{redirectTo}</a> : "—", cls: "sm-url" },
    { text: r.tags.join(", "), node: r.tags.length ? <span className="sm-clip">{r.tags.join(", ")}</span> : "—", cls: "sm-tags" },
    { text: r.metaTitle, node: r.metaTitle || "—", cls: "sm-meta" },
    { text: r.metaDescription, node: r.metaDescription || "—", cls: "sm-meta" },
    // Editor Type: how the page is currently built (read-only). "—" until the site's plugin is v1.24.6+.
    (() => { const et = editorTypeLabel(r.editorType); return { text: et.label, node: <span style={{ fontWeight: 700, color: et.color }}>{et.label}</span>, cls: "sm-editor" }; })(),
    // Schema: display the state label; the held-to-edit value is the raw LSG _audit_schema JSON.
    { text: sc.label, node: <span style={{ fontWeight: 700, color: sc.color }}>{sc.label}</span>, cls: "sm-schema", editValue: r.schemaJson ?? "" },
    { text: String(r.h1), node: r.h1, cls: "sm-num" },
    { text: String(r.h2), node: r.h2, cls: "sm-num" },
    { text: String(r.h3), node: r.h3, cls: "sm-num" },
    { text: String(r.h4), node: r.h4, cls: "sm-num" },
    { text: String(r.h5), node: r.h5, cls: "sm-num" },
    { text: String(r.h6), node: r.h6, cls: "sm-num" },
    { text: String(r.wordCount), node: r.wordCount.toLocaleString(), cls: "sm-num" },
    { text: String(r.statusCode ?? "ERR"), node: <span style={{ fontWeight: 700, color: statusColor(r.statusCode) }}>{r.statusCode ?? "ERR"}</span>, cls: "sm-num" },
    ];
  };
  // Column-width lock for the windowed table: auto table layout sizes columns from the rows in the
  // DOM, so as rows scroll in/out the columns resized and the table's horizontal position jumped.
  // A collapsed sizer row carries the longest few values of each column from the FULL list, so the
  // widths are the same whichever rows are rendered.
  const sizerCells = useMemo(() => {
    const TOP = 3;
    const best: { len: number; row: SitemapRow }[][] = [];
    for (const r of rows) {
      cellsFor(r).forEach((c, ci) => {
        const len = c.text.length;
        const list = (best[ci] ??= []);
        if (list.length < TOP || len > list[list.length - 1].len) {
          list.push({ len, row: r });
          list.sort((a, b) => b.len - a.len);
          if (list.length > TOP) list.pop();
        }
      });
    }
    return best.map((list, ci) => list.map(b => cellsFor(b.row)[ci]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, redirectByPath]);
  const handleGridKeyDown = (e: { ctrlKey: boolean; metaKey: boolean; key: string; preventDefault: () => void }) => {
    if (!sel) return;
    if (!((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C"))) return;
    const b = selBox(sel);
    const lines: string[] = [];
    for (let ri = b.r0; ri <= b.r1; ri++) {
      const row = displayRows[ri];
      if (!row) continue;
      const cells = cellsFor(row);
      const vals: string[] = [];
      for (let ci = b.c0; ci <= b.c1; ci++) vals.push(cells[ci]?.text ?? "");
      lines.push(vals.join("\t"));
    }
    const tsv = lines.join("\n");
    if (!tsv) return;
    e.preventDefault();
    writeClipboard(tsv);
  };

  if (!connected) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", color: "#64748b", fontFamily: "'Inter','Mulish',sans-serif" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Connect WordPress first</div>
        <div style={{ fontSize: 13 }}>The Sitemap reads your pages &amp; posts (and their IDs) from the connected WordPress site. Connect your site, then click Run Crawl.</div>
      </div>
    );
  }

  const showEmpty = !rows.length && !crawling;

  return (
    <div className="sitemap-tab">
      {crawledAt && !crawling && (
        <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "Roboto Mono, monospace", marginBottom: 8 }}>
          Last crawl: {crawledAt} · {rows.length} page{rows.length === 1 ? "" : "s"}
        </div>
      )}

      {showEmpty ? (
        <div style={{ padding: "40px 16px", textAlign: "center", color: "#64748b", fontFamily: "'Inter','Mulish',sans-serif" }}>
          <div className="es-title" style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>No data yet</div>
          <div className="es-desc" style={{ fontSize: 13 }}>Click <strong style={{ color: "#16a34a" }}>Run Crawl</strong> to list every page &amp; post with its SEO + structural stats.</div>
        </div>
      ) : rows.length ? (
        <>
        <div className="sitemap-scroll-top" ref={topRef} onScroll={() => syncFrom(topRef.current)}>
          <div style={{ width: tableWidth || "100%", height: 1 }} />
        </div>
        <div className="sitemap-scroll" ref={scrollRef} onScroll={() => { syncFrom(scrollRef.current); onScrollVertical(); }} tabIndex={0} onKeyDown={handleGridKeyDown} style={{ outline: "none" }}>
          <table className="sitemap-table" ref={tableRef}>
            <thead>
              <tr>
                <th className="sm-rownum">Row</th>
                <th className="sm-pick sm-check">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && rows.every(r => selected.has(rowKey(r)))}
                    onChange={e => onToggleAll(rows.map(rowKey), e.target.checked)}
                    title="Select all"
                  />
                </th>
                <th className="sm-pick">Recrawl</th>
                <th className="sm-editcol">Edit</th>
                {COLS.map(c => {
                  const sk = SORT_KEYS[c];
                  if (!sk) return <th key={c}>{c}</th>;
                  const active = sort?.key === sk;
                  return (
                    <th key={c} className="sm-sortable" onClick={() => toggleSort(sk)} title={sk === "schema" ? "Sort by schema: Added by LSG · Existing · No" : sk === "editor" ? "Sort by editor type: Classic - Markdown · Classic · Block · Elementor · Other" : "Sort alphabetically"}>
                      {c} <span className="sm-sort-ind">{active ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr aria-hidden="true" className="sm-sizer">
                <td className="sm-rownum">{displayRows.length}</td>
                <td className="sm-pick sm-check"><input type="checkbox" tabIndex={-1} readOnly /></td>
                <td className="sm-pick"><button className="sm-refresh-btn" tabIndex={-1}>↻</button></td>
                <td className="sm-editcol"><button className="sm-edit-btn" tabIndex={-1}>Edit Page</button></td>
                {sizerCells.map((list, ci) => (
                  <td key={ci} className={list[0]?.cls}>{list.map((c, j) => <div key={j}>{c.node}</div>)}</td>
                ))}
              </tr>
              {win.start > 0 && (
                <tr aria-hidden="true"><td colSpan={COLS.length + 4} style={{ height: win.start * rowHeight, padding: 0, border: "none" }} /></tr>
              )}
              {displayRows.slice(win.start, win.end).map((r, i) => {
                const ri = win.start + i; // absolute index in displayRows (selection/copy/row-number)
                const k = rowKey(r);
                const isRefreshing = refreshing.has(k);
                return (
                <tr key={k} data-smrow="" style={isRefreshing ? { opacity: 0.5 } : undefined}>
                  <td className="sm-rownum">{ri + 1}</td>
                  <td className="sm-pick sm-check">
                    <input type="checkbox" checked={selected.has(k)} onChange={() => onToggleSelect(k)} />
                  </td>
                  <td className="sm-pick">
                    <button
                      className="sm-refresh-btn"
                      disabled={isRefreshing}
                      title="Re-crawl this page"
                      onClick={() => onRefreshRow(k)}
                    >{isRefreshing ? "⟳" : "↻"}</button>
                  </td>
                  <td className="sm-editcol">
                    <button className="sm-edit-btn" title={r.type === "post" ? "Edit this post" : "Edit this page"} onClick={() => onEditPage(k)}>{r.type === "post" ? "Edit Post" : "Edit Page"}</button>
                  </td>
                  {cellsFor(r).map((c, ci) => (
                    <td
                      key={ci}
                      className={`${c.cls}${isSelected(ri, ci) ? " sm-cell-selected" : ""}${(EDITABLE[ci] || ci === URL_COL) ? " sm-editable" : ""}`}
                      onMouseDown={e => { holdFired.current = false; startSel(ri, ci, e); beginHold(k, ci, c.editValue ?? c.text, e.currentTarget); }}
                      onMouseEnter={() => extendSel(ri, ci)}
                      onMouseLeave={cancelHold}
                      onClickCapture={e => { if (holdFired.current) { e.preventDefault(); e.stopPropagation(); holdFired.current = false; } }}
                    >{c.node}</td>
                  ))}
                </tr>
                );
              })}
              {win.end < displayRows.length && (
                <tr aria-hidden="true"><td colSpan={COLS.length + 4} style={{ height: (displayRows.length - win.end) * rowHeight, padding: 0, border: "none" }} /></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      ) : null}

      {/* Hold-to-edit: "Edit?" confirm popup, anchored just below the held cell. */}
      {editPrompt && (
        <div
          style={{ position: "fixed", top: Math.min(editPrompt.rect.bottom + 4, window.innerHeight - 70), left: Math.min(editPrompt.rect.left, window.innerWidth - 190), zIndex: 3000, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.18)", padding: "9px 11px", fontFamily: "'Inter','Mulish',sans-serif", userSelect: "none" }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ fontSize: 12, color: "#334155", marginBottom: 8, fontWeight: 600 }}>Edit {editPrompt.label}?</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => setEditPrompt(null)} style={POP_CANCEL_BTN}>Cancel</button>
            <button
              onClick={() => {
                if (editPrompt.kind === "parent") { onEditParent(editPrompt.key); setEditPrompt(null); }
                else { setEditing(editPrompt); setEditPrompt(null); }
              }}
              style={POP_PRIMARY_BTN}
            >Yes</button>
          </div>
        </div>
      )}

      {/* Hold-to-edit: the edit box, anchored just below the held cell so dragging its resize
          handle downward grows the box downward. */}
      {editing && (
        <div
          style={{ position: "fixed", top: Math.min(editing.rect.bottom + 4, Math.max(8, window.innerHeight - 440)), left: Math.min(editing.rect.left, window.innerWidth - 460), zIndex: 3000, width: 440, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.22)", padding: 12, fontFamily: "'Inter','Mulish',sans-serif", userSelect: "text" }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: .6, color: "#64748b", marginBottom: 6 }}>{editing.label}</div>
          <textarea
            autoFocus
            value={editing.value}
            ref={autosizeTextarea}
            onChange={e => { setEditing(s => (s ? { ...s, value: e.target.value } : s)); autosizeTextarea(e.currentTarget); }}
            rows={1}
            style={{ width: "100%", boxSizing: "border-box", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, lineHeight: 1.45, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none", resize: "vertical", overflow: "auto", maxHeight: 340 }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button onClick={() => setEditing(null)} style={POP_CANCEL_BTN}>Cancel</button>
            <button onClick={() => { const ed = editing; setEditing(null); void onEditCell(ed.key, ed.field, ed.value); }} style={POP_PRIMARY_BTN}>Submit</button>
          </div>
        </div>
      )}
    </div>
  );
});
